/**
 * Serve tokens in content URLs. The share view's content iframe loads `/s/<x>/<file>`, and the
 * content's own scripts can read that URL. With the SESSION as `<x>` they read a bearer for the
 * whole share session plus its readable identity (email, org, link token). The server seals the
 * session into a SERVE token (GET /api/share-links/session/<sid>/serve-token, also in `/info`):
 * opaque, and accepted only by the `/s/` routes. Every URL handed to content goes through
 * `useServeTokenUrl`; the SPA's own fetches, downloads and API headers keep the session.
 */
import { useEffect, useState } from 'react'
import { currentSessionSignal, getApiBase, sessionEndedSince, sessionFetch } from '@papan/api-client'

const SERVE_TOKEN_PREFIX = 'pst1_'
const tokens = new Map<string, string>()
/** What a serve-token fetch answers (see `fetchServeToken`). */
export type ServeTokenAnswer = string | 'fallback' | 'retry' | 'ended'
// Each in-flight fetch with the session it was sent in: a caller starting after that session ended never joins it.
const inflight = new Map<string, { p: Promise<ServeTokenAnswer>; start: AbortSignal | undefined }>()

/** Record a serve token the server already returned (e.g. in `/info`) — no fetch for it. */
export function seedServeToken(sessionId: string, serveToken: unknown): void {
  if (sessionId && typeof serveToken === 'string' && serveToken.startsWith(SERVE_TOKEN_PREFIX)) tokens.set(sessionId, serveToken)
}

/** The session segment of a `/s/<segment>/…` URL (absolute or root-relative), or null. */
export function serveSegmentOf(url: string): string | null {
  const m = /\/s\/([^/?#]+)/.exec(url.replace(/^[a-z]+:\/\/[^/]+/i, ''))
  return m ? m[1]! : null
}

/** A stored relative path as a serve URL's path: each segment percent-encoded, so a name
 *  holding `#`, `?`, `%` or `+` reaches the server whole (it decodes each segment once). */
export function encodeServePath(relPath: string): string {
  return relPath.split('/').map(encodeURIComponent).join('/')
}

/** `url` with its `/s/<segment>` replaced by `/s/<token>` (only the first, the serve prefix). */
export function withServeToken(url: string, segment: string, token: string): string {
  return url.replace(`/s/${segment}`, `/s/${token}`)
}

/**
 * Fetch (once per session, de-duplicated) the serve token. `'fallback'` = a 404: an API that has
 * no such route (an older instance mid rolling deploy) or a session that is gone — whose URL
 * serves nothing either way, so the session URL may be used as is. `'retry'` = anything else (a
 * network blip, a 5xx, a 429, a revocation check that could not run): the session must NOT be
 * handed to the content for it, so the caller keeps the iframe unmounted and asks again. `'ended'` (#1316) = it failed
 * because the session it was asked in ended: nothing is retried (a retry would ask as the NEXT session).
 */
export function fetchServeToken(sessionId: string): Promise<ServeTokenAnswer> {
  const cached = tokens.get(sessionId)
  if (cached) return Promise.resolve(cached)
  const joined = inflight.get(sessionId)
  if (joined && !sessionEndedSince(joined.start)) return joined.p
  const start = currentSessionSignal()
  const p: Promise<ServeTokenAnswer> = sessionFetch(`${getApiBase()}/api/share-links/session/${encodeURIComponent(sessionId)}/serve-token`, { credentials: 'include' })
    .then(async (res): Promise<ServeTokenAnswer> => {
      if (res.status === 404) return 'fallback'
      if (!res.ok) return 'retry'
      const body = (await res.json().catch(() => ({}))) as { serveToken?: unknown }
      seedServeToken(sessionId, body.serveToken)
      return tokens.get(sessionId) ?? 'retry'
    })
    .catch((err: unknown): 'retry' | 'ended' => (sessionEndedSince(start, err) ? 'ended' : 'retry'))
    .finally(() => { if (inflight.get(sessionId)?.p === p) inflight.delete(sessionId) })
  inflight.set(sessionId, { p, start })
  return p
}

/** Backoff between serve-token attempts (ms), capped. */
export function serveTokenRetryDelay(attempt: number): number {
  return Math.min(15_000, 1_000 * 2 ** Math.max(0, attempt))
}

/**
 * The content-facing form of a `/s/<session>/…` URL: the same URL with the session's serve
 * token in its place. `null` while the token is being fetched — the caller renders the iframe
 * only once this is non-null, so content never loads with the session in its URL. Only a 404
 * (an older API mid rolling deploy, or a dead session — whose URL serves nothing anyway) falls
 * back to the URL as given; any other failure retries with backoff and keeps it null. A URL
 * already on a serve token, or that is not a `/s/` URL, is returned as is.
 */
export function useServeTokenUrl(url: string | null): string | null {
  const segment = url ? serveSegmentOf(url) : null
  const direct = !url ? null
    : !segment || segment.startsWith(SERVE_TOKEN_PREFIX) ? url
    : tokens.has(segment) ? withServeToken(url, segment, tokens.get(segment)!)
    : null
  const [resolved, setResolved] = useState<{ url: string; out: string } | null>(null)
  useEffect(() => {
    if (!url || direct || !segment) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    // #1316: the flow (first attempt and every backoff retry) belongs to the session it started in — a retry due
    // after that session ended is never sent (it would ask as the NEXT session)
    const start = currentSessionSignal()
    const attempt = (n: number) => {
      if (sessionEndedSince(start)) return
      void fetchServeToken(segment).then((r) => {
        if (cancelled || r === 'ended') return   // #1316: its session ended — nothing written, nothing asked again
        if (r === 'retry') { timer = setTimeout(() => attempt(n + 1), serveTokenRetryDelay(n)); return }
        setResolved({ url, out: r === 'fallback' ? url : withServeToken(url, segment, r) })
      })
    }
    attempt(0)
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [url, direct, segment])
  if (direct) return direct
  return resolved && resolved.url === url ? resolved.out : null
}

/**
 * #989 D3: a share session's serve token, for URLs the share view itself puts on the preview
 * host (downloads, the inline viewer, the favicon, the format probe). The preview host never
 * sees the browser-binding cookie, so a RAW session there is refused once its visit binds —
 * these URLs carry the serve token instead. `null` until it is known (seeded from `/info`, else
 * fetched once, retried on a transient failure); a gone session (404) stays `null`.
 */
export function useServeTokenFor(sessionId: string | null | undefined): string | null {
  return useServeTokenState(sessionId).token
}

/**
 * {@link useServeTokenFor}, saying whether the session is GONE: `/serve-token` answered 404 — a
 * session that no longer exists (or an API too old to have the route). The caller must not wait
 * for a token that will never come: it asks `/info` what happened (re-entry, an error, or an old
 * API whose raw session URL still serves).
 */
export function useServeTokenState(sessionId: string | null | undefined): { token: string | null; gone: boolean } {
  const cached = sessionId ? tokens.get(sessionId) ?? null : null
  const [fetched, setFetched] = useState<{ sid: string; token: string | null; gone: boolean } | null>(null)
  useEffect(() => {
    if (!sessionId || cached) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const start = currentSessionSignal()   // #1316 — as `useServeTokenUrl`
    const attempt = (n: number) => {
      if (sessionEndedSince(start)) return
      void fetchServeToken(sessionId).then((r) => {
        if (cancelled || r === 'ended') return   // #1316 — as above
        if (r === 'retry') { timer = setTimeout(() => attempt(n + 1), serveTokenRetryDelay(n)); return }
        setFetched(r === 'fallback' ? { sid: sessionId, token: null, gone: true } : { sid: sessionId, token: r, gone: false })
      })
    }
    attempt(0)
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [sessionId, cached])
  if (cached) return { token: cached, gone: false }
  return fetched && fetched.sid === sessionId ? { token: fetched.token, gone: fetched.gone } : { token: null, gone: false }
}

